/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/*
* Add new layer to the map
* User can select which bands to visualize as RGB
*/

import * as Blockly from 'blockly/core';
import 'blockly/python';
import { getAllowedOutputs } from '../block_compatibility_checks';
import { getCurrentSatUsingRootBlock, getBandArray } from '../dataset';

const compMethods = [
    ["Median", "median"],
    ["Mean", "mean"],
    ["Max", "max"],
    ["Min", "min"],
    ["Mosaic", "mosaic"],
    //["Most Recent", "mostRecent"]
]

const config = {
    default_sat: "l8_SR",
    red: {
        default: "SR_B4",
        str_id: "bandR"
    },
    green: {
        default: "SR_B3",
        str_id: "bandG"
    },
    blue: {
        default: "SR_B2",
        str_id: "bandB"
    }
}

Blockly.Blocks['output_optical_vis'] = {
    init: function () {

        const generateVisOptions = (function (self) {
            return function () {
                let sat = getCurrentSatUsingRootBlock(self);
                if ((sat !== null) && (sat !== '')) {
                    return getBandArray[sat]();
                } else {
                    return getBandArray[config.default_sat]();
                }
            }
        })(this);

        let layerNameValidator = newValue => {
            let maxLength = 20;
            let cleanValue = newValue.replace(/[^a-zA-Z0-9 ]/g, "");
            let trimmedValue = cleanValue.substring(0, maxLength)
            return trimmedValue;
        }

        let dropdown = generateVisOptions;

        // TODO create a dropdown validate function that updates the band names to be something like
        // ["Actual Band Name", "R"] etc. rather than just Red, Green, Blue
        this.bands = [
            ['Red', 'R'],
            ['Green', 'G'],
            ['Blue', 'B']
        ];

        this.appendDummyInput('bands')
            .appendField("Bands to visualise: ")
            .appendField("R ", "r")
            .appendField(new Blockly.FieldDropdown(dropdown), config.red.str_id)
            .appendField(" G ", "g")
            .appendField(new Blockly.FieldDropdown(dropdown), config.green.str_id)
            .appendField(" B ", "b")
            .appendField(new Blockly.FieldDropdown(dropdown), config.blue.str_id);

        this.getField(config.red.str_id).setValue(config.red.default);
        this.getField(config.green.str_id).setValue(config.green.default);
        this.getField(config.blue.str_id).setValue(config.blue.default);

        this.appendDummyInput()
            .appendField("Composite Method:  ")
            .appendField(new Blockly.FieldDropdown(compMethods), "comp_method")
            .appendField("  Layer name: ")
            .appendField(new Blockly.FieldTextInput("default", layerNameValidator), "layerName");
            
        this.getField("layerName").setTooltip(Blockly.Msg.LAYER_NAME_TOOLTIP)

        this.setPreviousStatement(true, 'rgb_vis');
        this.setNextStatement(true, getAllowedOutputs('rgb_vis'));
        this.setStyle('mapVisualisation');
        this.setTooltip(Blockly.Msg.ADDLAYER_BLOCK_TOOLTIP);
        this.setHelpUrl("");
        
        var previousSat = config.default_sat
        this.setOnChange(function () {
            let newSat = getCurrentSatUsingRootBlock(this);
            if (newSat != previousSat && newSat !== null) {
                this.updateShape_(newSat, previousSat)
                previousSat = newSat
            }
        }
        );
    },

    updateShape_: function (newSat, previousSat) {
        console.log('newSat', newSat, 'previousSat', previousSat);

        let dropdown = getBandArray[newSat]();
        console.log("dropdown", dropdown);
        let oldBands = getBandArray[previousSat]();
        console.log("oldBands", oldBands);

        let r = this.getFieldValue(config.red.str_id);
        let g = this.getFieldValue(config.green.str_id);
        let b = this.getFieldValue(config.blue.str_id);

        this.getInput('bands').removeField(config.red.str_id);
        this.getInput('bands').removeField(config.green.str_id);
        this.getInput('bands').removeField(config.blue.str_id);

        this.getInput('bands').removeField('r');
        this.getInput('bands').removeField('g');
        this.getInput('bands').removeField('b');

        this.getInput('bands').appendField("R ", "r")
        this.getInput('bands').appendField(new Blockly.FieldDropdown(dropdown), config.red.str_id);
        this.getInput('bands').appendField(" G ", "g")
        this.getInput('bands').appendField(new Blockly.FieldDropdown(dropdown), config.green.str_id);
        this.getInput('bands').appendField(" B ", "b")
        this.getInput('bands').appendField(new Blockly.FieldDropdown(dropdown), config.blue.str_id);

        // function that check whether the same band selection is available 
        // when switching satellite input and selects the equivalent options
        function getEquivalentBand(oldBand, newBands, oldBands) {
            // initialise old band name
            let oldBandName = null;
            // set up a default band value
            let newBandValue = newBands[0][1];
            // get old band name by matching up with current old band value
            for (const band in oldBands) {
                if (oldBands[band][1].slice(-2) === oldBand.slice(-2)) {
                    let extractedOldName = oldBands[band][0].match(/\((.*?)\)/);
                    oldBandName = extractedOldName[1];
                }
            }
            
            // now match the old band name with the equivalent option from the new satellite bands, if there is one
            for (const band in newBands) {
                let bandName = newBands[band][0];

                // get band name in () for matching up with new band
                let matchName = bandName.match(/\((.*?)\)/);
                let newBandName = matchName[1];

                if (newBandName === oldBandName) {
                    // set the new band to the equivalent if it exists
                    newBandValue = newBands[band][1];
                }
            }
            return newBandValue;
        }

        // set bands to correspond to field names, ie. R is Red band, B is Blue band, G is green band
        this.getField(config.red.str_id).setValue(getEquivalentBand(r, dropdown, oldBands));
        this.getField(config.green.str_id).setValue(getEquivalentBand(g, dropdown, oldBands));
        this.getField(config.blue.str_id).setValue(getEquivalentBand(b, dropdown, oldBands));
    },
};